'use client';

import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SetupStepFooterProps {
  backLabel: string;
  onBack: () => void;
  backDisabled?: boolean;
  backHidden?: boolean;
  backType?: 'button' | 'submit';
  isBackLoading?: boolean;
  /** Default: ArrowLeft. Pass false for text-only back. */
  backIcon?: ReactNode | false;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  primaryHidden?: boolean;
  primaryType?: 'button' | 'submit';
  isPrimaryLoading?: boolean;
  primaryIcon?: boolean;
  /** Left-aligned slot used in place of the Back button (e.g. a Skip control on the first step). */
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

/** Back left · primary right — same bar on every setup step. */
export function SetupStepFooter({
  backLabel,
  onBack,
  backDisabled = false,
  backHidden = false,
  backType = 'button',
  isBackLoading = false,
  backIcon,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryHidden = false,
  primaryType = 'button',
  isPrimaryLoading = false,
  primaryIcon = true,
  leading,
  trailing,
  className,
}: SetupStepFooterProps) {
  const showBackIcon = backIcon !== false;
  const backIconNode =
    backIcon === false ? null : backIcon === undefined ? (
      <ArrowLeft className="size-4" strokeWidth={2} aria-hidden />
    ) : (
      backIcon
    );

  return (
    <div className={cn('flex w-full items-center justify-between gap-4', className)}>
      {backHidden ? (
        leading ? (
          <div className="mr-auto flex shrink-0 items-center">{leading}</div>
        ) : (
          <span className="mr-auto w-0 shrink-0" aria-hidden />
        )
      ) : (
        <Button
          type={backType}
          variant="outline"
          className="mr-auto shrink-0"
          onClick={backType === 'button' ? onBack : undefined}
          disabled={backDisabled || isBackLoading}
        >
          {isBackLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {showBackIcon && !isBackLoading ? backIconNode : null}
          {backLabel}
        </Button>
      )}

      {trailing || !primaryHidden ? (
        <div className="ml-auto flex shrink-0 items-center justify-end gap-3">
          {trailing}
          {!primaryHidden && primaryLabel ? (
            <Button
              type={primaryType}
              className="shrink-0"
              disabled={primaryDisabled || isPrimaryLoading}
              onClick={primaryType === 'button' ? onPrimary : undefined}
            >
              {isPrimaryLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span>{primaryLabel}</span>
              {primaryIcon && !isPrimaryLoading ? (
                <ArrowRight className="size-4" strokeWidth={2.25} aria-hidden />
              ) : null}
            </Button>
          ) : null}
        </div>
      ) : (
        <span className="ml-auto w-0 shrink-0" aria-hidden />
      )}
    </div>
  );
}
